import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Crown, Sparkles } from "lucide-react";
import confetti from 'canvas-confetti';

// Play victory fanfare sound
const playVictorySound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const playTone = (freq, startTime, duration) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = freq;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.2, startTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };
    const now = audioContext.currentTime;
    playTone(392, now, 0.15);
    playTone(392, now + 0.15, 0.15);
    playTone(392, now + 0.3, 0.15);
    playTone(311, now + 0.45, 0.3);
    playTone(349, now + 0.75, 0.15);
    playTone(392, now + 0.9, 0.15);
    playTone(311, now + 1.05, 0.2);
    playTone(523, now + 1.25, 0.5);
  } catch (e) {}
};

// Play drum roll sound for reveal
const playDrumRoll = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const now = audioContext.currentTime;
    for (let i = 0; i < 8; i++) {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 150 + Math.random() * 50;
      oscillator.type = 'triangle';
      gainNode.gain.setValueAtTime(0.1, now + i * 0.08);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + i * 0.08 + 0.06);
      oscillator.start(now + i * 0.08);
      oscillator.stop(now + i * 0.08 + 0.06);
    }
  } catch (e) {}
};

// Trigger confetti explosion
const triggerConfetti = () => {
  const duration = 5000;
  const end = Date.now() + duration;
  const colors = ['#FFD700', '#E91E8C', '#8B5CF6', '#00E676', '#FF1744', '#CCFF00'];
  
  (function frame() {
    confetti({
      particleCount: 7,
      angle: 60,
      spread: 65,
      origin: { x: 0, y: 0.6 },
      colors: colors
    });
    confetti({
      particleCount: 7,
      angle: 120,
      spread: 65,
      origin: { x: 1, y: 0.6 },
      colors: colors
    });
    
    if (Date.now() < end) {
      requestAnimationFrame(frame);
    }
  }());
};

const getTeamName = (team) => team.name || `Team ${team.number}`;

const FinalScoresReveal = ({ scoreboard, onClose }) => {
  const [revealedTeams, setRevealedTeams] = useState([]);
  const [showWinner, setShowWinner] = useState(false);
  const revealStartedRef = useRef(false);

  useEffect(() => {
    if (!scoreboard || scoreboard.length === 0) return;
    if (revealStartedRef.current) return;
    
    revealStartedRef.current = true;
    
    // Only show top 3 teams
    const top3 = scoreboard.slice(0, 3);
    const totalTeams = top3.length;
    let currentRank = totalTeams; // Start at 3rd place
    let currentIndex = totalTeams - 1; // Index into top3 (3rd place first)
    
    const revealNext = () => {
      if (currentIndex >= 0) {
        playDrumRoll();
        setTimeout(() => {
          const team = top3[currentIndex];
          const teamWithRank = { ...team, rank: currentRank };
          setRevealedTeams(prev => [teamWithRank, ...prev]); // Prepend so newest appears at top
          
          currentIndex--;
          currentRank--;
          
          if (currentIndex >= 0) {
            setTimeout(revealNext, 1500); // Delay between reveals
          } else {
            // All revealed - show winner celebration
            setTimeout(() => {
              setShowWinner(true);
              playVictorySound();
              triggerConfetti();
            }, 1000);
          }
        }, 500);
      }
    };
    
    // Start revealing after a brief pause
    setTimeout(revealNext, 500);
    
  }, []); // Empty deps - only run once

  if (!scoreboard || scoreboard.length === 0) return null;

  const winner = scoreboard[0];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 overflow-auto"
    >
      <div className="w-full max-w-lg">
        {/* Header */}
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-center mb-6"
        >
          <div className="flex items-center justify-center gap-3 mb-2">
            <Sparkles className="w-8 h-8 text-[#FFD700]" />
            <h1 className="text-3xl md:text-4xl font-bold text-white">Final Results</h1>
            <Sparkles className="w-8 h-8 text-[#FFD700]" />
          </div>
        </motion.div>

        {/* Revealed teams list */}
        <div className="space-y-3">
          <AnimatePresence mode="popLayout">
            {revealedTeams.map((team) => {
              const actualRank = team.rank;
              const isWinner = actualRank === 1;
              
              return (
                <motion.div
                  key={team.number}
                  initial={{ x: -150, opacity: 0, scale: 0.5, rotateY: -90 }}
                  animate={{ x: 0, opacity: 1, scale: 1, rotateY: 0 }}
                  transition={{ 
                    type: "spring", 
                    stiffness: 200, 
                    damping: 20,
                    duration: 0.8
                  }}
                  className={`flex items-center gap-4 p-4 rounded-xl border-2 ${
                    actualRank === 1 ? 'border-[#FFD700] bg-gradient-to-r from-[#FFD700]/40 to-[#FFA500]/20' :
                    actualRank === 2 ? 'border-[#C0C0C0] bg-gradient-to-r from-[#C0C0C0]/30 to-transparent' :
                    actualRank === 3 ? 'border-[#CD7F32] bg-gradient-to-r from-[#CD7F32]/30 to-transparent' :
                    'border-white/20 bg-white/10'
                  }`}
                >
                  {/* Rank badge */}
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.3, type: "spring" }}
                    className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
                      actualRank === 1 ? 'bg-[#FFD700] text-black' :
                      actualRank === 2 ? 'bg-[#C0C0C0] text-black' :
                      actualRank === 3 ? 'bg-[#CD7F32] text-black' :
                      'bg-white/20 text-white'
                    }`}
                  >
                    {actualRank === 1 ? <Crown className="w-6 h-6" /> : actualRank}
                  </motion.div>

                  {/* Team name */}
                  <div className="flex-1">
                    <p className={`text-xl font-bold ${isWinner ? 'text-[#FFD700]' : 'text-white'}`}>
                      {getTeamName(team)}
                    </p>
                    {actualRank <= 3 && (
                      <p className="text-xs text-[#E2D9F3]">
                        {actualRank === 1 ? '🥇 1st Place' : actualRank === 2 ? '🥈 2nd Place' : '🥉 3rd Place'}
                      </p>
                    )}
                  </div>

                  {/* Score */}
                  <div className="text-right">
                    <span className="text-2xl font-bold text-white">{team.score}</span>
                    <span className="text-sm text-[#E2D9F3] ml-1">pts</span>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {/* Winner celebration */}
        <AnimatePresence>
          {showWinner && winner && (
            <motion.div
              initial={{ scale: 0, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              transition={{ type: "spring", stiffness: 150, damping: 12 }}
              className="mt-8 text-center"
            >
              <motion.div
                animate={{ 
                  boxShadow: [
                    "0 0 20px rgba(255, 215, 0, 0.3)",
                    "0 0 50px rgba(255, 215, 0, 0.6)",
                    "0 0 20px rgba(255, 215, 0, 0.3)"
                  ]
                }}
                transition={{ duration: 2, repeat: Infinity }}
                className="inline-block p-6 rounded-2xl bg-gradient-to-br from-[#FFD700]/40 to-[#FFA500]/20 border-4 border-[#FFD700]"
              >
                <motion.div
                  animate={{ rotate: [0, -5, 5, 0] }}
                  transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
                >
                  <Trophy className="w-16 h-16 text-[#FFD700] mx-auto mb-3" />
                </motion.div>
                <h2 className="text-2xl font-bold text-[#FFD700] mb-2">🎉 WINNER 🎉</h2>
                <motion.p
                  className="text-3xl font-bold text-white"
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  {getTeamName(winner)}
                </motion.p>
                <p className="text-xl text-[#E2D9F3] mt-2">{winner.score} points</p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default FinalScoresReveal;
